import {SearchSuggestionTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/search/v1/service.types';
import {SearchNameType} from '@aboutyou/legacy-tracking-sdk';
import {
  createPath,
  useHistory,
  useInitialLocation,
  useSearchRoute,
} from '@aboutyou/router';
import {useSearchEventsUtils, useTrackSearch} from '@aboutyou/tracking';
import {useKeyPress} from '@aboutyou/ui-components';
import debounce from 'lodash.debounce';
import {useCallback, useRef} from 'react';
import {SEARCH_BAR_ANIMATION_TIME_MS} from '../../constants';
import {onClickSuggestionType} from './useSearchBar';
import {useStoredSearchTerms} from './useStoredSearchTerms';

export type SearchSuggestionState = {wasSearchPerformed?: boolean};

const TRACK_SUGGEST_DEBOUNCE_DELAY_MS = 500;

const searchDebounceConfig = {
  debounceTime: 300,
  options: {
    leading: true,
    maxWait: 300,
  },
};

export const useSearchSuggestions = ({
  isSearchBarExpanded,
  isSearchInTabletMode,
  collapseAndBlurInput,
  handleInputOnChange,
  searchInputRef,
  searchValue,
  handleRemoveInputFocus,
  searchWrapperRef,
}: {
  isSearchBarExpanded: boolean;
  isSearchInTabletMode: boolean;
  collapseAndBlurInput: (shouldCollapse?: boolean) => void;
  handleInputOnChange: (term: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchValue: string;
  handleRemoveInputFocus: VoidFunction;
  searchWrapperRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const {addSearchTerm} = useStoredSearchTerms({limitSearchHistory: true});
  const trackSearch = useTrackSearch();
  const searchRoute = useSearchRoute();
  const {updateSearchEventContextParams} = useSearchEventsUtils();
  const history = useHistory();
  const tabletOpenButtonRef = useRef<HTMLButtonElement>(null);

  const clearSearchAndFocus = () => {
    trackSearch({
      searchAction: 'click',
      searchName: 'searchClose',
    });

    handleInputOnChange('');

    if (searchInputRef.current) {
      searchInputRef.current.value = '';
      searchInputRef.current.focus();
    }

    if (isSearchBarExpanded) {
      collapseAndBlurInput();
    }
  };

  const clearButtonOnKeyDownHandler = (
    e?: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    const isSpaceOrEnterPressed = e?.key === 'Space' || e?.key === 'Enter';

    if (!isSpaceOrEnterPressed) {
      return null;
    }

    clearSearchAndFocus();
  };

  const handleOnEscape = () => {
    if (!searchWrapperRef?.current?.contains(document.activeElement)) {
      return;
    }

    collapseAndBlurInput();

    // need to wait for the search bar animation to finish to be able to blur or focus on other element
    setTimeout(() => {
      isSearchInTabletMode
        ? tabletOpenButtonRef.current?.focus()
        : searchInputRef.current?.blur();
    }, SEARCH_BAR_ANIMATION_TIME_MS);
  };

  useKeyPress({
    key: 'Escape',
    onKeyDown: handleOnEscape,
    disabled: false,
  });

  const createSearchPath = (searchTerm: string) => {
    const searchQueryParams = new URLSearchParams();
    searchQueryParams.append('term', searchTerm);

    const searchPathWithTerm = createPath({
      pathname: searchRoute,
      search: searchQueryParams.toString(),
    });

    return searchPathWithTerm;
  };

  const resolveSearchTerm = async ({
    searchTerm,
    searchName,
    searchTermCompleted,
  }: {
    searchTerm: string;
    searchName: SearchNameType;
    searchTermCompleted?: string;
  }) => {
    const term = searchTermCompleted || searchTerm;

    const trackedSearchTerm =
      searchName === 'historyTerm' ? searchTermCompleted : searchTerm;

    updateSearchEventContextParams({
      searchTerm: trackedSearchTerm,
      searchName,
      searchTermCompleted,
    });

    addSearchTerm({term});

    const searchQueryParams = new URLSearchParams();
    searchQueryParams.append('term', term);

    history.push({
      pathname: searchRoute,
      search: searchQueryParams.toString(),
      state: {wasSearchPerformed: true} as SearchSuggestionState,
    });
  };

  const onSearchInputEnter = () => {
    const searchTerm = searchInputRef.current?.value.trim() || '';
    if (!searchTerm) {
      return;
    }

    collapseAndBlurInput();

    resolveSearchTerm({searchTerm, searchName: 'searchHotkey'});
  };

  const onClickSuggestion: onClickSuggestionType = ({
    searchTerm,
    searchType,
  }) => {
    if (!searchTerm) {
      return;
    }

    if (searchInputRef.current) {
      searchInputRef.current.value = searchTerm;
    }

    handleInputOnChange(searchTerm);
    handleRemoveInputFocus();

    resolveSearchTerm({
      searchTerm: searchValue,
      searchName: searchType,
      searchTermCompleted: searchTerm,
    });
  };

  const handleInputOnChangeWithDebounce = useCallback(
    debounce(
      (searchValue: string) => {
        handleInputOnChange(searchValue);
      },
      searchDebounceConfig.debounceTime,
      searchDebounceConfig.options,
    ),
    [],
  );

  const handleInputOnChangeTrackingWithDebounce = useCallback(
    debounce((searchValue: string) => {
      trackSearch({searchAction: 'search_suggest', searchTerm: searchValue});
    }, TRACK_SUGGEST_DEBOUNCE_DELAY_MS),
    [],
  );

  const shouldShowCloseIcon = Boolean(
    (isSearchInTabletMode && isSearchBarExpanded) ||
      (!isSearchInTabletMode && searchValue.length),
  );

  return {
    shouldShowCloseIcon,
    onClickSuggestion,
    onSearchInputEnter,
    clearSearchAndFocus,
    createSearchPath,
    handleInputOnChangeTrackingWithDebounce,
    handleInputOnChangeWithDebounce,
    clearButtonOnKeyDownHandler,
    tabletOpenButtonRef,
  };
};

export const getSearchSuggestionName = (type: SearchSuggestionTypes) => {
  const searchNamesByValueMap: Record<SearchSuggestionTypes, SearchNameType> = {
    [SearchSuggestionTypes.UNSPECIFIED]: 'unspecified',
    [SearchSuggestionTypes.TERMS]: 'suggestedTerm',
    [SearchSuggestionTypes.BRANDS]: 'suggestedBrand',
  };

  return searchNamesByValueMap[type];
};

export const useFilterOutSearchParameters = () => {
  const {protocol, host} = useInitialLocation();

  const filterOutSearchParameters = (url: string) => {
    const redirectUrl = new URL(`${protocol}://${host}${url}`);
    redirectUrl.searchParams.delete('is_h');
    redirectUrl.searchParams.delete('is_s');

    if (url.startsWith('/search')) {
      redirectUrl.searchParams.delete('search_type');
    } else {
      redirectUrl.searchParams.delete('term');
    }

    return `${redirectUrl.pathname}${redirectUrl.search}`;
  };

  return filterOutSearchParameters;
};
